import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Shield,
  Globe,
} from "lucide-react";
import Button from "component/ui/Button";
import Input from "component/ui/Input";
import DateAndTimePicker from "component/ui/Datepicker";
import PageHeading from "../../../component/ui/PageHeading";
import SelectField from "component/ui/SelectField";
import { getCountryCode, isValidNumber, validEmail } from "helper/appConstant";
import { useLoginData } from "component/common/loginData";
import { PhoneInput } from "react-international-phone";
import { PhoneNumber } from "component/ui/PhoneNumber";
import LocationPicker from "component/ui/LocationPicker";
import { toast } from "react-toastify";
import {
  addBankAccountOnRazorPay,
  addBankAccountOnStripe,
  uploadFileOnLocal,
} from "store/slice/booking";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import FullPageLoader from "component/ui/fullPageLoader";

const AddAccount = () => {
  const [countryCode, setCountryCode] = useState("AU");
  const userData = useLoginData();
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getRequiredFields = () => {
    if (isIndiaView) {
      return [
        // 1. Personal Information
        { key: "userName", label: "Full Name" },
        { key: "email", label: "Email" },
        { key: "legalBusinessName", label: "Business Name" },
        { key: "businessType", label: "Business Type" },
        { key: "phone", label: "Phone Number" },

        // 2. Address Information
        { key: "addressLine1", label: "Address Line 1" },
        { key: "addressLine2", label: "Address Line 2" },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "postal_code", label: "Postal Code" },

        // // 3. Legal
        // { key: "panNumber", label: "PAN Number" },

        // 4. Bank
        { key: "accountHolder", label: "Account Holder Type" },
        { key: "accountType", label: "Bank Account Type" },
        { key: "accountNumber", label: "Account Number" },
        { key: "confirmAccountNumber", label: "Confirm Account Number" },
        { key: "ifscCode", label: "IFSC Code" },
        { key: "beneficiaryName", label: "Beneficiary Name" },
      ];
    }

    // 🇦🇺 Australia (exact screen order)
    return [
      // 1. Personal Information
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "dob", label: "Date of birth" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone Number" },

      // 2. Address Information
      { key: "addressLine1", label: "Address Line 1" },
      { key: "addressLine2", label: "Address Line 2" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "postal_code", label: "Postal Code" },

      // 3. Bank Information
      { key: "accountHolder", label: "Account Holder Type" },

      { key: "accountNumber", label: "Account Number" },
      { key: "confirmAccountNumber", label: "Confirm Account Number" },
      { key: "routingNumber", label: "BSB number" },
      { key: "frontDocumentId", label: "Front document" },
      { key: "backDocumentId", label: "Back document" },
    ];
  };
  const validateCompanyFields = () => {
    const companyRequiredFields = [
      { key: "companyName", label: "Company Name" },
      { key: "companyPhone", label: "Company Phone" },
      { key: "companyTaxId", label: "Company Tax ID" },
      { key: "companyAddress", label: "Company Address" },
      { key: "companyCity", label: "Company City" },
      { key: "companyState", label: "Company State" },
      { key: "companyPostalCode", label: "Company Postal Code" },
    ];

    for (let field of companyRequiredFields) {
      const value = formData?.[field.key];

      if (!value || (typeof value === "string" && !value.trim())) {
        toast.error(`${field.label} is required`);
        return false;
      }
      // phone
      if (field?.key === "companyPhone" && !isValidNumber(value?.slice(3))) {
        toast.error("Please enter a valid company phone number");
        return;
      }
    }

    return true;
  };

  const handleNavigate = () => navigate("/payment-methods");

  // get country
  useEffect(() => {
    const fetchCountryCode = async () => {
      const code = await getCountryCode(userData);
      setCountryCode(code); // 👈 set value here
    };

    fetchCountryCode();
  }, [userData]);

  // set field value
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(" name, value", name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // add bank account
  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredFields = getRequiredFields();

    for (let field of requiredFields) {
      const value = formData?.[field.key];

      if (!value || (typeof value === "string" && !value.trim())) {
        toast.error(`${field.label} is required`);
        return; //  stop submission
      }

      // Email format validation (exact position)
      if (field?.key === "email" && !validEmail(value)) {
        toast.error("Please enter a valid email address");
        return;
      }

      // phone
      if (field?.key === "phone" && !isValidNumber(value?.slice(3))) {
        toast.error("Please enter a valid phone number");
        return;
      }
    }
    //  AU + Company validation
    if (!isIndiaView && formData.accountHolder === "Company") {
      if (!validateCompanyFields()) return;
    }
    // Extra validation
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      toast.error("Account number and confirm account number must match");
      return;
    }
    setLoader(true);
    if (countryCode == "IN") {
      const request = {
        ...formData,
        language: "en",
        country: countryCode,
        profileInfo: {
          category: null,
          subcategory: null,
          addresses: {
            registered: {
              street1: formData?.addressLine1,
              street2: formData?.addressLine2,
              city: formData?.city,
              state: formData?.state,
              postal_code: formData?.postal_code,
              country: countryCode == "IN" ? "India" : "Australia",
            },
          },
        },
      };
      dispatch(addBankAccountOnRazorPay(request, handleNavigate,setLoader));
    } else {
      const request = {
        ...formData,
        language: "en",
        country: countryCode,
        postalCode: formData?.postal_code,
      };
      dispatch(addBankAccountOnStripe(request, handleNavigate,setLoader));
    }
  };

  const isIndiaView = countryCode == "IN";

  // docment upload
  const handleUploadFile = async (file, type) => {
    if (!file) return;
    const formData = new FormData();
    if (typeof file === "object" && file !== null) {
      Object.entries(file).forEach(([key, value]) => {
        formData.append("file", value);
      });
    }
    try {
      // Dispatch the upload action
      const data = await dispatch(uploadFileOnLocal(formData, "stripeDoc", 1));
      if (data) {
        setFormData((formData) => ({
          ...formData,
          [type]: data?.stripeDocId,
        }));
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };
  return (
    <div className="min-h-screen">
      <div className="lg:max-w-5xl w-full mx-auto lg:p-8 p-4">
        {loader && <FullPageLoader />}
        <div className="text-sm">
          <span
            onClick={handleNavigate}
            className="text-gray-500 cursor-pointer hover:underline hover:text-highlight"
          >
            Payment Method /
          </span>
          <span className="text-highlight">Add Bank Account</span>
        </div>
        <PageHeading
          title={"Bank Account Setup"}
          Subtitle={"Complete your account details securely "}
        />

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-2xl md:p-6 p-3 w-full max-w-full overflow-x-hidden">
          <form onSubmit={handleSubmit}>
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6 pb-4 border-b-2 border-blue-100">
                <div className="p-1 bg-blue-100 rounded-md">
                  <User className="md:w-5 md:h-5 w-4 h-4 text-[#2bace2]" />
                </div>
                <h2 className="md:text-xl text-lg font-medium text-gray-800">
                  Personal Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                {isIndiaView ? (
                  <div className="space-y-2 ">
                    <Input
                      label="Full Name (Contact Name)"
                      isForm
                      isRequired
                      type="text"
                      value={formData?.userName}
                      name="userName"
                      placeholder="Abhay Vishwakarma"
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Input
                        label="First Name"
                        isForm
                        isRequired
                        type="text"
                        name="firstName"
                        value={formData?.firstName}
                        placeholder="John"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        label="Last Name"
                        isForm
                        isRequired
                        type="text"
                        name="lastName"
                        value={formData?.lastName}
                        placeholder="Doe"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="date"
                        name="dob"
                        onChange={(val) =>
                          setFormData((formData) => ({
                            ...formData,
                            dob: val,
                          }))
                        }
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Input
                    label="Email"
                    isForm
                    isRequired
                    type="text"
                    value={formData?.email}
                    name="email"
                    placeholder="your.email@example.com"
                    onChange={handleChange}
                    required
                  />
                </div>

                {isIndiaView && (
                  <>
                    <div className="space-y-2">
                      <Input
                        label=" Business Name "
                        isForm
                        isRequired
                        value={formData?.legalBusinessName}
                        type="text"
                        name="legalBusinessName"
                        placeholder="Business name"
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Business type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="businessType"
                        value={formData.businessType || ""}
                        onChange={handleChange}
                        style={{
                          color: formData.businessType ? "" : "gray",
                          marginTop: "0.2rem",
                          padding: 11.5,
                        }}
                        className="text-sm w-full bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#2BACE2]"
                      >
                        <option disabled className="text-gray-400" value="">
                          Select business type
                        </option>
                        <option value="individual">Individual</option>
                        <option value="partnership">Partnership</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 ">
                      Phone
                      <span className="text-red-500 ml-1 font-bold">*</span>
                    </label>
                    <PhoneNumber
                      inputClassName="w-full !py-3 placeholder:text-sm text-sm transition-all duration-300 !bg-gray-50 hover:bg-white relative  "
                      onChange={(val) =>
                        setFormData((formData) => ({
                          ...formData,
                          phone: val?.countryCode + val?.phoneNumber,
                        }))
                      }
                      phone={formData?.phone}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Address Information */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6 pb-4 border-b-2 border-purple-100">
                <div className="p-1 bg-blue-100 rounded-md">
                  <MapPin className="md:w-5 md:h-5 w-4 h-4 text-[#2bace2]" />
                </div>
                <h2 className="md:text-xl text-lg font-medium text-gray-800">
                  Address Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 ">
                    Address Line 1
                    <span className="text-red-500 ml-1 font-bold">*</span>
                  </label>
                  <div className="pt-1">
                    <LocationPicker
                      onChange={(place) => {
                        setFormData((prev) => ({
                          ...prev,
                          addressLine1: place?.formatted_address,
                        }));
                      }}
                      value={formData?.addressLine1}
                      placeholder={"Enter address 1"}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 ">
                    Address Line 2
                    <span className="text-red-500 ml-1 font-bold">*</span>
                  </label>
                  <div className="pt-1">
                    <LocationPicker
                      onChange={(place) => {
                        setFormData((prev) => ({
                          ...prev,
                          addressLine2: place?.formatted_address,
                        }));
                      }}
                      value={formData?.addressLine2}
                      placeholder={"Enter address 2"}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Input
                    label="City"
                    isForm
                    isRequired
                    type="text"
                    name="city"
                    value={formData?.city}
                    placeholder="City name"
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    label="State"
                    isForm
                    isRequired
                    type="text"
                    name="state"
                    value={formData?.state}
                    placeholder="Madhya Pradesh"
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    label="Postal Code"
                    isForm
                    isRequired
                    type="text"
                    name="postal_code"
                    maxLength="6"
                    value={formData?.postal_code}
                    placeholder="PIN/ZIP code"
                    onChange={handleChange}
                    required
                  />
                </div>

                <Input
                  disabled
                  label="Country"
                  value={countryCode == "IN" ? "India" : "Australia"}
                  required
                />
              </div>
            </div>

            {/* 3. Legal Information */}
            {isIndiaView && (
              <div className="mb-12">
                <div className="flex items-center space-x-3 mb-6 pb-4 border-b-2 border-green-100">
                  <div className="p-1 bg-blue-100 rounded-md">
                    <Shield className="md:w-6 md:h-6 w-4 h-4 text-[#2bace2]" />
                  </div>
                  <h2 className="md:text-xl text-lg font-medium text-gray-800">
                    Legal Information
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                  <div className="space-y-2">
                    <Input
                      label="PAN Number"
                      isForm
                      type="text"
                      name="panNumber"
                      value={formData?.panNumber}
                      placeholder="ABCDE1234F"
                      maxLength="10"
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Input
                      label="GST Number"
                      isForm
                      type="text"
                      name="gstNumber"
                      value={formData?.gstNumber}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength="15"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 4. Bank Information */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-6 pb-4 border-b-2 border-indigo-100">
                <div className="p-1 bg-indigo-100 rounded-md">
                  <CreditCard className="md:w-5 md:h-5 w-4 h-4 text-[#2bace2]" />
                </div>
                <h2 className="md:text-xl text-lg font-medium text-gray-800">
                  Bank Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <div className="space-y-2">
                  <SelectField
                    label="Account Holder Type"
                    isRequired
                    id="level-select"
                    value={formData.accountHolder || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        accountHolder: value,
                      }))
                    }
                    options={["Individual", "Company"]}
                    placeholder="Select holder type"
                  />
                </div>
                {!isIndiaView && formData.accountHolder == "Company" && (
                  <>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company Name"
                        isRequired
                        name="companyName"
                        value={formData?.companyName}
                        placeholder="Enter company name"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 ">
                          Company Phone
                          <span className="text-red-500 ml-1 font-bold">*</span>
                        </label>
                        <PhoneNumber
                          inputClassName="w-full !py-3 placeholder:text-sm text-sm transition-all duration-300 !bg-gray-50 hover:bg-white relative  "
                          onChange={(val) =>
                            setFormData((formData) => ({
                              ...formData,
                              companyPhone: val?.countryCode + val?.phoneNumber,
                            }))
                          }
                          phone={formData?.companyPhone}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company Tax Id"
                        isRequired
                        name="companyTaxId"
                        value={formData?.companyTaxId}
                        placeholder="Enter company tax id"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company Address"
                        isRequired
                        name="companyAddress"
                        value={formData?.companyAddress}
                        placeholder="Enter company address"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company City"
                        isRequired
                        name="companyCity"
                        value={formData?.companyCity}
                        placeholder="Enter company city"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company State"
                        isRequired
                        name="companyState"
                        value={formData?.companyState}
                        placeholder="Enter company state"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Input
                        isForm
                        label="Company postal code"
                        isRequired
                        name="companyPostalCode"
                        value={formData?.companyPostalCode}
                        placeholder="Enter company postal code"
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </>
                )}
                {isIndiaView && (
                  <div className="space-y-2">
                    <SelectField
                      label="Bank account type"
                      isRequired
                      id="level-select"
                      value={formData.accountType}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          accountType: value,
                        }))
                      }
                      options={["Current", "Savings"]}
                      placeholder="Select bank account"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Input
                    isForm
                    label="Bank Account Number"
                    isRequired
                    name="accountNumber"
                    maxLength={20}
                    value={formData?.accountNumber}
                    placeholder="Enter account number"
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    label="Confirm Account Number"
                    isForm
                    isRequired
                    type="text"
                    maxLength={20}
                    name="confirmAccountNumber"
                    placeholder="Re-enter account number"
                    value={formData?.confirmAccountNumber}
                    onChange={handleChange}
                    required
                  />
                </div>

                {isIndiaView ? (
                  <>
                    <div className="space-y-2">
                      <Input
                        label="IFSC Code"
                        isForm
                        isRequired
                        type="text"
                        name="ifscCode"
                        placeholder="ABCD0123456"
                        maxLength="11"
                        value={formData?.ifscCode}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2 ">
                      <Input
                        label="Beneficiary name"
                        isForm
                        isRequired
                        type="text"
                        name="beneficiaryName"
                        value={formData?.beneficiaryName}
                        placeholder="Beneficiary name"
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Input
                        label="BSB number"
                        isForm
                        isRequired
                        type="text"
                        name="routingNumber"
                        value={formData?.routingNumber}
                        placeholder="BSB number"
                        maxLength="11"
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Upload document <span className="text-red-500">*</span>
                      </label>
                      <div className="relative group">
                        <input
                          type="file"
                          onChange={(event) =>
                            handleUploadFile(
                              event.target.files,
                              "frontDocumentId"
                            )
                          }
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#2bace2]  border-2 border-dashed border-gray-300 rounded-xl p-1.5 cursor-pointer transition-all hover:border-[#2bace2]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Upload document <span className="text-red-500">*</span>
                      </label>
                      <div className="relative group">
                        <input
                          type="file"
                          name="addressProof"
                          onChange={(event) =>
                            handleUploadFile(
                              event.target.files,
                              "backDocumentId"
                            )
                          }
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#2bace2] border-2 border-dashed border-gray-300 rounded-xl p-1.5 cursor-pointer transition-all hover:border-[#2bace2]"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t-2 border-gray-100">
              <div className="flex justify-center">
                <Button type="submit">Submit Application</Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAccount;
